import type { DomainItem } from '@/entities/domain';

export type DomainTreeNode = {
  key: string;
  value: string;
  title: string;
  children?: DomainTreeNode[];
  description?: string;
  data: DomainItem;
};

/**
 * Flat 리스트 형태의 도메인 데이터를 Tree 구조로 변환합니다.
 *
 * @description
 * OpenMetadata API는 모든 도메인을 Flat 리스트로 반환하며,
 * 각 도메인은 parent 필드로 부모 관계를 표현합니다.
 * 이 함수는 Flat 리스트를 Tree 구조의 DomainTreeNode로 변환합니다.
 *
 * @param domains - Flat 리스트 형태의 도메인 배열
 * @returns Tree 구조의 DomainTreeNode 배열 (루트 노드)
 */
export const buildDomainTree = (domains: DomainItem[]): DomainTreeNode[] => {
  const domainMap = new Map<string, DomainTreeNode>();
  const rootNodes: DomainTreeNode[] = [];

  domains.forEach((domain) => {
    const node: DomainTreeNode = {
      key: domain.id,
      value: domain.name,
      title: domain.displayName || domain.name,
      children: [],
      description: domain.description,
      data: domain,
    };
    domainMap.set(domain.id, node);
  });

  domains.forEach((domain) => {
    const node = domainMap.get(domain.id);
    if (!node) return;

    if (domain.parent) {
      const parentNode = domainMap.get(domain.parent.id);
      if (parentNode) {
        if (!parentNode.children) {
          parentNode.children = [];
        }
        parentNode.children.push(node);
      } else {
        rootNodes.push(node);
      }
    } else {
      rootNodes.push(node);
    }
  });

  const sortNodes = (nodes: DomainTreeNode[]): DomainTreeNode[] => {
    return nodes
      .sort((a, b) => a.title.localeCompare(b.title, 'ko', { numeric: true }))
      .map((node) => {
        if (node.children && node.children.length > 0) {
          return {
            ...node,
            children: sortNodes(node.children),
          };
        }
        return node;
      });
  };

  return sortNodes(rootNodes);
};
